/**
 * Socrates V2 API Client
 * RESTful API 기반의 새로운 소크라테스 면접 시스템 클라이언트
 */

// 프록시를 통해 Socrates V2 API 호출 (mixed content 우회)
// 개발 환경: setupProxy.js에서 /api/* -> http://donga-sci2.jobshopper.net으로 프록시
// 프로덕션 환경: 환경 변수 사용
// const BASE_URL = process.env.NODE_ENV === 'production'
//   ? process.env.REACT_APP_NEW_SOCRATES_BASE_URL
//   : '/api';

const BASE_URL = process.env.REACT_APP_NEW_SOCRATES_BASE_URL;

// 공통 fetch 옵션 (Android WebView CORS 대응)
const CORS_OPTIONS = {
  mode: 'cors',
  // credentials: 'include',
  // headers: {
  //   'X-Requested-With': 'XMLHttpRequest'
  // }
};

class SocratesAPI {
  /**
   * 인터뷰 생성
   * @param {FormData} formData - multipart/form-data 형식의 폼 데이터
   * @returns {Promise<{interviewId: number}>}
   */
  async createInterview(formData) {
    const response = await fetch(`${BASE_URL}/interviews`, {
      method: 'POST',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        // Content-Type은 FormData가 자동 설정하므로 생략
      },
      body: formData
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 조회
   * @param {number} interviewId
   * @returns {Promise<Interview>}
   */
  async getInterview(interviewId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}`, {
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 삭제
   * @param {number} interviewId
   * @returns {Promise<null>}
   */
  async deleteInterview(interviewId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}`, {
      method: 'DELETE',
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 채팅 - 답변 제출 및 다음 질문 받기
   * @param {number} interviewId
   * @param {string} userId
   * @param {string} content - 사용자 답변 내용
   * @param {AbortSignal} [signal] - 요청 취소용 signal (optional)
   * @returns {Promise<{messageId: number, responseContent: string}>}
   */
  async chat(interviewId, userId, content, signal) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/chat`, {
      method: 'POST',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userId, content}),
      signal // AbortController signal 전달
    });
    return this.handleResponse(response);
  }

  /**
   * 메시지 목록 조회
   * @param {number} interviewId
   * @param {number} limit - 최대 메시지 수 (-1: 전체)
   * @returns {Promise<{messages: ChatMessage[]}>}
   */
  async getMessages(interviewId, limit = -1) {
    const response = await fetch(
      `${BASE_URL}/interviews/${interviewId}/messages?limit=${limit}`,
      { ...CORS_OPTIONS }
    );
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 결과 생성
   * @param {number} interviewId
   * @returns {Promise<null>}
   */
  async generateResult(interviewId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/result`, {
      method: 'POST',
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 재시작
   * @param {number} interviewId
   * @param {string} userId
   * @returns {Promise<null>}
   */
  async restartInterview(interviewId, userId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/restart`, {
      method: 'POST',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userId})
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 복제
   * @param {number} interviewId
   * @param {string} userId
   * @returns {Promise<{interviewId: number}>}
   */
  async cloneInterview(interviewId, userId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/clone`, {
      method: 'POST',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userId})
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 일시정지
   * @param {number} interviewId
   * @param {string} userId
   * @returns {Promise<null>}
   */
  async pauseInterview(interviewId, userId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/pause`, {
      method: 'PUT',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userId})
    });
    return this.handleResponse(response);
  }

  /**
   * 인터뷰 재개
   * @param {number} interviewId
   * @param {string} userId
   * @returns {Promise<null>}
   */
  async resumeInterview(interviewId, userId) {
    const response = await fetch(`${BASE_URL}/interviews/${interviewId}/resume`, {
      method: 'PUT',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userId})
    });
    return this.handleResponse(response);
  }

  /**
   * 사용자별 인터뷰 목록 조회
   * @param {string} userId
   * @returns {Promise<InterviewListItem[]>}
   */
  async getUserInterviews(userId) {
    if (!userId) {
      console.warn('[socratesApi] getUserInterviews: userId가 없습니다.');
      return [];
    }
    const response = await fetch(`${BASE_URL}/users/${userId}/interviews`, {
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 사용자별 인터뷰 전체 삭제
   * @param {string} userId
   * @returns {Promise<null>}
   */
  async deleteUserInterviews(userId) {
    const response = await fetch(`${BASE_URL}/users/${userId}/interviews`, {
      method: 'DELETE',
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 면접 유형 목록 조회 (Legacy Backend)
   * @returns {Promise<{kind_list: Array<{id: number, name: string}>}>}
   */
  async getInterviewCategories() {
    const legacyBaseUrl = process.env.REACT_APP_BASE_URL;
    const endpoint = process.env.REACT_APP_SOCRATES_GET_KIND;
    
    const response = await fetch(`${legacyBaseUrl}${endpoint}`, {
      method: 'GET',
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 관심 영역 목록 조회 (Legacy Backend)
   * @returns {Promise<{inter_area_list: Array<{id: number, name: string}>}>}
   */
  async getInterestingAreas() {
    const legacyBaseUrl = process.env.REACT_APP_BASE_URL;
    const endpoint = process.env.REACT_APP_SOCRATES_GET_INTERESTING;
    
    const response = await fetch(`${legacyBaseUrl}${endpoint}`, {
      method: 'GET',
      ...CORS_OPTIONS
    });
    return this.handleResponse(response);
  }

  /**
   * 파일 업로드 (텍스트 추출)
   * @param {FormData} formData - 업로드할 파일이 포함된 FormData
   * @returns {Promise<{result: string}>} 추출된 텍스트 내용
   */
  async uploadFile(formData) {
    const uploadUrl = process.env.REACT_APP_SOCRATES_UPLOAD;
    
    const response = await fetch(uploadUrl, {
      method: 'POST',
      ...CORS_OPTIONS,
      headers: {
        ...CORS_OPTIONS.headers,
        // Content-Type은 FormData가 자동 설정하므로 생략
      },
      body: formData
    });
    return this.handleResponse(response);
  }

  /**
   * 공통 응답 처리
   * @private
   */
  async handleResponse(response) {
    if (!response.ok) {
      let errorMessage = '요청 실패';

      try {
        const error = await response.json();
        errorMessage = error.error?.message || errorMessage;

        // 필드별 에러가 있는 경우 포맷팅
        if (error.error?.fields) {
          const fieldErrors = Object.values(error.error.fields).join(', ');
          errorMessage = `${errorMessage}: ${fieldErrors}`;
        }
      } catch (e) {
        // JSON 파싱 실패 시 기본 에러 메시지 사용
        errorMessage = `${errorMessage} (${response.status} ${response.statusText})`;
      }

      throw new Error(errorMessage);
    }

    // 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }
}

export default new SocratesAPI();
